import { useState, useRef, useCallback, useEffect } from 'react';
import styles from './MarkdownEditor.module.css';

const TOOLBAR_ACTIONS = [
  { icon: 'fa-heading', title: 'Heading', mode: 'line', prefix: '### ' },
  { icon: 'fa-bold', title: 'Bold', mode: 'wrap', prefix: '**', suffix: '**' },
  {
    icon: 'fa-italic',
    title: 'Italic',
    mode: 'wrap',
    prefix: '_',
    suffix: '_',
  },
  {
    icon: 'fa-underline',
    title: 'Underline',
    mode: 'wrap',
    prefix: '<u>',
    suffix: '</u>',
  },
  { icon: 'fa-quote-left', title: 'Quote', mode: 'line', prefix: '> ' },
  { icon: 'fa-link', title: 'Link', mode: 'link' },
  { icon: 'fa-list-ol', title: 'Ordered List', mode: 'line', prefix: '1. ' },
  { icon: 'fa-list-ul', title: 'Unordered List', mode: 'line', prefix: '- ' },
];

const HISTORY_LIMIT = 100;
const DEBOUNCE_MS = 300;

const MarkdownEditor = ({ value, onChange, placeholder, rows = 8 }) => {
  const textareaRef = useRef(null);
  const linkButtonRef = useRef(null);
  const historyRef = useRef([value || '']);
  const historyIndexRef = useRef(0);
  const lastValueRef = useRef(value || '');
  const debounceRef = useRef(null);
  const selectionRef = useRef({ start: 0, end: 0 });
  const [linkPopoverOpen, setLinkPopoverOpen] = useState(false);
  const [linkText, setLinkText] = useState('');
  const [linkUrl, setLinkUrl] = useState('');

  useEffect(() => {
    if (value !== lastValueRef.current) {
      historyRef.current = [value || ''];
      historyIndexRef.current = 0;
      lastValueRef.current = value || '';
    }
  }, [value]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  const pushHistory = useCallback(val => {
    const history = historyRef.current;
    const index = historyIndexRef.current;

    if (history[index] === val) return;

    historyRef.current = history.slice(0, index + 1);
    historyRef.current.push(val);

    if (historyRef.current.length > HISTORY_LIMIT) {
      historyRef.current = historyRef.current.slice(-HISTORY_LIMIT);
    }
    historyIndexRef.current = historyRef.current.length - 1;
  }, []);

  const updateValue = useCallback(
    newValue => {
      lastValueRef.current = newValue;
      onChange(newValue);
    },
    [onChange],
  );

  const flushDebounce = useCallback(() => {
    if (debounceRef.current) {
      clearTimeout(debounceRef.current);
      debounceRef.current = null;
      const currentVal = lastValueRef.current;
      if (historyRef.current[historyIndexRef.current] !== currentVal) {
        pushHistory(currentVal);
      }
    }
  }, [pushHistory]);

  const undo = useCallback(() => {
    flushDebounce();

    if (historyIndexRef.current > 0) {
      historyIndexRef.current -= 1;
      updateValue(historyRef.current[historyIndexRef.current]);
    }
  }, [flushDebounce, updateValue]);

  const redo = useCallback(() => {
    if (historyIndexRef.current < historyRef.current.length - 1) {
      historyIndexRef.current += 1;
      updateValue(historyRef.current[historyIndexRef.current]);
    }
  }, [updateValue]);

  const handleKeyDown = useCallback(
    e => {
      const isMod = e.ctrlKey || e.metaKey;
      if (!isMod) return;

      if (e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        undo();
      } else if (e.key === 'z' && e.shiftKey) {
        e.preventDefault();
        redo();
      } else if (e.key === 'y') {
        e.preventDefault();
        redo();
      }
    },
    [undo, redo],
  );

  const handleTextareaChange = useCallback(
    e => {
      const newValue = e.target.value;
      updateValue(newValue);

      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        pushHistory(newValue);
        debounceRef.current = null;
      }, DEBOUNCE_MS);
    },
    [updateValue, pushHistory],
  );

  const openLinkPopover = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    flushDebounce();

    const { selectionStart, selectionEnd } = textarea;
    const text = value || '';
    const selected = text.slice(selectionStart, selectionEnd);

    selectionRef.current = { start: selectionStart, end: selectionEnd };
    setLinkText(selected);
    setLinkUrl('');
    setLinkPopoverOpen(true);
  }, [value, flushDebounce]);

  const closeLinkPopover = useCallback(() => {
    setLinkPopoverOpen(false);
    setLinkText('');
    setLinkUrl('');
  }, []);

  const insertLink = useCallback(() => {
    const textarea = textareaRef.current;
    if (!textarea || !linkUrl) return;

    const { start, end } = selectionRef.current;
    const text = value || '';
    const markdown = `[${linkText}](${linkUrl})`;
    const newText = text.slice(0, start) + markdown + text.slice(end);
    const cursorPos = start + markdown.length;

    updateValue(newText);
    pushHistory(newText);
    closeLinkPopover();

    requestAnimationFrame(() => {
      textarea.focus();
      textarea.setSelectionRange(cursorPos, cursorPos);
    });
  }, [value, linkText, linkUrl, updateValue, pushHistory, closeLinkPopover]);

  const applyAction = useCallback(
    action => {
      if (action.mode === 'link') {
        openLinkPopover();
        return;
      }

      const textarea = textareaRef.current;
      if (!textarea) return;

      flushDebounce();

      const { selectionStart, selectionEnd } = textarea;
      const text = value || '';
      const selected = text.slice(selectionStart, selectionEnd);

      let newText;
      let cursorPos;

      if (action.mode === 'wrap') {
        const wrapped = `${action.prefix}${selected}${action.suffix}`;
        newText =
          text.slice(0, selectionStart) + wrapped + text.slice(selectionEnd);
        if (selected) {
          cursorPos = selectionStart + wrapped.length;
        } else {
          cursorPos = selectionStart + action.prefix.length;
        }
      } else if (action.mode === 'line') {
        const lineStart = text.lastIndexOf('\n', selectionStart - 1) + 1;
        newText =
          text.slice(0, lineStart) + action.prefix + text.slice(lineStart);
        cursorPos = selectionStart + action.prefix.length;
      }

      updateValue(newText);
      pushHistory(newText);

      requestAnimationFrame(() => {
        textarea.focus();
        textarea.setSelectionRange(cursorPos, cursorPos);
      });
    },
    [value, updateValue, pushHistory, flushDebounce, openLinkPopover],
  );

  return (
    <div className={styles.container}>
      <div className={styles.toolbar}>
        {TOOLBAR_ACTIONS.map(action => (
          <button
            key={action.icon}
            type="button"
            ref={action.mode === 'link' ? linkButtonRef : undefined}
            className={styles.toolbarButton}
            title={action.title}
            onClick={() => applyAction(action)}
          >
            <i className={`fa-solid ${action.icon}`} />
          </button>
        ))}
      </div>
      {linkPopoverOpen && (
        <>
          <div
            className={styles.linkPopoverOverlay}
            onClick={closeLinkPopover}
          />
          <div
            className={styles.linkPopover}
            onKeyDown={e => {
              if (e.key === 'Escape') closeLinkPopover();
            }}
          >
            <label className={styles.linkPopoverField}>
              <span>Text</span>
              <input
                type="text"
                value={linkText}
                onChange={e => setLinkText(e.target.value)}
                autoFocus
                onKeyDown={e => {
                  if (e.key === 'Enter' && linkUrl) {
                    e.preventDefault();
                    insertLink();
                  }
                }}
              />
            </label>
            <label className={styles.linkPopoverField}>
              <span>URL</span>
              <input
                type="text"
                value={linkUrl}
                onChange={e => setLinkUrl(e.target.value)}
                onKeyDown={e => {
                  if (e.key === 'Enter' && linkUrl) {
                    e.preventDefault();
                    insertLink();
                  }
                }}
              />
            </label>
          </div>
        </>
      )}
      <textarea
        ref={textareaRef}
        className={styles.textarea}
        value={value}
        onChange={handleTextareaChange}
        onKeyDown={handleKeyDown}
        placeholder={placeholder}
        rows={rows}
      />
    </div>
  );
};

export default MarkdownEditor;
